import { useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip, Typography } from '@mui/material';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon.svg';
import Breadcrumbs from '../../../components/Breadcrumbs';
import GenerateAIButton from '../../../components/GenerateAIButton';
import { Flex, FlexCenter, FlexColumn, FlexSpace } from '../../../components';
import { UrlFilters } from '../../../hooks/useUrlFilters';
import { colors } from '../../../style/theme';
import { Invocation } from '../../../types';
import { isInSaaSMode, sumArr } from '../../../utils/common';
import { localizeDate } from '../../../utils/datetime';
import { TestType, buildTestTypeFilters } from '../../../utils/testTypes';
import { ChartData, PieChartData, SummaryData } from '../types';
import useViewLoadTracking from '../../../hooks/useViewLoadTracking';
import {
  TEST_RESULTS_TABLE_FILTERS,
  MODEL_RUNS_TABLE_FILTERS,
  TEST_RUNS_TABLE_FILTERS,
} from '../../../constants/urlParams';
import { FilterOption, FilterPopover } from '../../../components';
import useAccount from '../../../hooks/useAccount';
import useContainerBreakpoint from '../../../hooks/useContainerBreakpoint';
import useDashboardFilters from '../hooks/useDashboardFilters';
import FilterChipRow from '../../../components/Table/TableFilters/FilterChipRow';
import DashboardChart from './DashboardChart';
import DashboardPieChart from './DashboardPieChart';
import Legend from './Legend';
import SummaryBox from './SummaryBox';

const tableStatusFilterMap: Record<string, string[]> = {
  failed: ['fail'],
  warning: ['warn'],
  passed: ['pass'],
  error: ['error'],
  fail: ['fail'],
  warn: ['warn'],
  pass: ['pass'],
};

const modelStatusFilterMap: Record<string, string[]> = {
  error: ['error'],
  skipped: ['skipped'],
  success: ['success'],
  'partial success': ['partial success'],
};

const testRunsStatusFilterMap: Record<string, string[]> = {
  failures: ['error', 'fail'],
  warnings: ['warn'],
  passed: ['pass'],
  errors: ['error', 'fail'],
};

const tooltips = {
  resultsBreakdown: 'Latest unique test results by status',
  tablesHealth: 'Health of tables based on the status of their test results',
  monitoredTablesPie: 'Tables with associated tests versus those without',
  freshness: 'Elementary freshness anomalies tests',
  volume: 'Elementary volume anomalies tests',
  schemaChanges: 'Elementary table and column schema tests',
  dbtTests:
    'Data assertion tests - including native dbt tests, dbt singular tests, dbt expectations, dbt utils tests and Python tests',
  anomalies: 'Elementary column and dimension anomalies tests',
  uniqueTestExecutions: 'Daily number of unique test executions overtime',
  testFailures: 'Daily number of unique test failures over time',
  testWarnings: 'Daily number of unique test warnings over time',
  tablesWithFailedTests:
    'The number of tables with at least one failed test over time',
  monitoredTables:
    'Percentage of tables covered by at least one test over time',
  modelRunErrors: 'Daily number of unique model run errors over time',
  modelRuns: 'Health of models based on the status of their last dbt run',
};

const taskRunsCopy = {
  runsTitle: 'Task runs',
  runsPieLabel: 'Tasks',
  runsTooltip: 'Health of tasks based on the status of their last run',
  errorsTitle: 'Task run errors',
  errorsTooltip: 'Daily number of unique task run errors over time',
};

const modelRunsCopy = {
  runsTitle: 'Model runs',
  runsPieLabel: 'Models',
  runsTooltip: tooltips.modelRuns,
  errorsTitle: 'Model run errors',
  errorsTooltip: tooltips.modelRunErrors,
};

const statusLegendCategories = [
  { name: 'Passed', color: colors.green },
  { name: 'Warning', color: colors.orange },
  { name: 'Failed', color: colors.red },
  { name: 'Error', color: colors.gray1 },
];

const modelStatusLegendCategories = [
  { name: 'Success', color: colors.green },
  { name: 'Partial', color: colors.orange },
  { name: 'Error', color: colors.red },
  { name: 'Skipped', color: colors.gray1 },
];

const monitoredLegendCategories = [
  { name: 'Monitored', color: colors.green },
  { name: 'Unmonitored', color: colors.gray1 },
];

const orderedSummaryKeys = [
  'freshness',
  'volume',
  'schemaChanges',
  'dbtTests',
  'anomalies',
];

const NARROW_BREAKPOINT = { key: 'narrow', width: 1300 };
const NARROW_SELECTOR = `[data-narrow] &`;

const DashboardContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const CenteringWrapper = styled(FlexColumn)`
  align-items: center;
  padding: 24px;
  padding-bottom: 24px;
  width: 1274px;
  max-width: 100%;
  gap: 24px;
  margin: 0 auto;

  ${NARROW_SELECTOR} {
    width: 852px;
  }
`;

const Header = styled(FlexColumn)`
  width: 100%;
  gap: 16px;
`;

const Filters = styled(FlexColumn)`
  gap: 24px;
`;

const StatusDivider = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;
  width: 1px;
  background: ${({ theme }) => theme.palette.common.gray1};
`;

const PieSectionDivider = styled.div`
  height: 200px;
  width: 1px;
  background: ${({ theme }) => theme.palette.common.gray1};

  ${NARROW_SELECTOR} {
    height: 1px;
    width: 100%;
  }
`;

const HorizontalDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.palette.common.gray1};
`;

const InvocationWrapper = styled(Flex)`
  background: ${({ theme }) => theme.palette.common.gray3};
  padding: 8px 16px;
  border-radius: 4px;
  height: fit-content;
`;

const InvocationText = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const StatusWrapper = styled(FlexColumn)`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.common.gray1};
  border-radius: 6px;
  gap: 24px;
  padding: 24px;
`;

const PieChartsWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;

  ${NARROW_SELECTOR} {
    flex-wrap: wrap;
    justify-content: center;
    gap: 24px;
  }
`;

const ModelRunChartsWrapper = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const ModelPieChartWrapper = styled(Flex)`
  gap: 24px;
  justify-content: space-around;
  padding: 24px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.common.gray1};
  border-radius: 6px;
  height: 260px;
`;

const SummaryWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;

  ${NARROW_SELECTOR} {
    flex-wrap: wrap;
    justify-content: space-evenly;
    row-gap: 24px;

    > :nth-child(6) {
      display: none;
    }
  }
`;

const ChartsWrapper = styled(FlexColumn)`
  gap: 24px;
  width: 100%;
`;

const ResponsiveChartContainer = styled.div<{
  $width: number;
  $narrowWidth: number;
}>`
  width: ${({ $width }) => $width}px;

  ${NARROW_SELECTOR} {
    width: ${({ $narrowWidth }) => $narrowWidth}px;
  }
`;

const ChartRow = styled(Flex)`
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 24px;

  ${NARROW_SELECTOR} {
    justify-content: center;
  }
`;

export type DashboardProps = {
  filterOptions: FilterOption[];
  resultsBreakdownData: PieChartData;
  tablesHealthData: PieChartData;
  monitoredTablesData: PieChartData;
  modelsHealthData: PieChartData;
  summaryData: SummaryData;
  uniqueTestExecData: ChartData;
  failedTestsChartData: ChartData;
  warningTestsChartData: ChartData;
  monitoredTablesChartData: ChartData;
  tablesWithTestFailuresChartData: ChartData;
  modelFailuresChartData: ChartData;
  statusChartsDomainYDataMax: number;
  testTypes: TestType[];
  daysBack: number;
  invocationData?: Invocation;
  renderEmptyStatePage: () => JSX.Element;
  isEmpty?: boolean;
  isLoading?: boolean;
  isLoaded?: boolean;
  breadcrumbs?: {
    label: string;
    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  }[];
  isDashboardStatsLoading?: boolean;
  isDashboardStatsLoaded?: boolean;
  showToggleTreeViewButton?: boolean;
};

const Dashboard = ({
  filterOptions,
  resultsBreakdownData,
  tablesHealthData,
  monitoredTablesData,
  modelsHealthData,
  summaryData,
  uniqueTestExecData,
  failedTestsChartData,
  warningTestsChartData,
  monitoredTablesChartData,
  tablesWithTestFailuresChartData,
  modelFailuresChartData,
  statusChartsDomainYDataMax,
  testTypes,
  daysBack,
  invocationData,
  renderEmptyStatePage,
  isEmpty,
  isLoading,
  isLoaded,
  breadcrumbs,
  isDashboardStatsLoading,
  isDashboardStatsLoaded,
  showToggleTreeViewButton = false,
}: DashboardProps) => {
  const { data: account } = useAccount();
  const { ref: containerRef } = useContainerBreakpoint([NARROW_BREAKPOINT]);
  const runsCopy = isInSaaSMode() ? taskRunsCopy : modelRunsCopy;

  const {
    urlFilters,
    clearAllFilters,
    clearFilter,
    navigateWithFilters,
    onFilterSubmit,
  } = useDashboardFilters();

  useViewLoadTracking({
    name: 'Dashboard',
    loadDeps: [isLoaded || null, isDashboardStatsLoaded || null],
    contextId: JSON.stringify(urlFilters),
  });

  const handleTestsPieLabelClick = (status: string) => {
    const queryParams = new URLSearchParams();

    queryParams.set(
      TEST_RESULTS_TABLE_FILTERS,
      JSON.stringify([
        {
          property: 'latestRunStatus',
          operator: 'equals',
          value: tableStatusFilterMap[status.toLowerCase()],
        },
      ]),
    );

    navigateWithFilters('/report/test-results', queryParams);
  };

  const handleTablesPieLabelClick = (status: string) => {
    const queryParams = new URLSearchParams();

    queryParams.set(
      TEST_RESULTS_TABLE_FILTERS,
      JSON.stringify([
        {
          property: 'latestRunStatus',
          operator: 'equals',
          value: tableStatusFilterMap[status.toLowerCase()],
        },
      ]),
    );

    navigateWithFilters('/report/test-results', queryParams);
  };

  const handleChartSeeResultsClick = (status: string) => {
    const queryParams = new URLSearchParams();
    queryParams.set(
      TEST_RUNS_TABLE_FILTERS,
      JSON.stringify([
        {
          property: 'metadata.latest_run_status',
          operator: 'equals',
          value: testRunsStatusFilterMap[status.toLowerCase()],
        },
      ]),
    );

    navigateWithFilters('/report/test-runs', queryParams);
  };

  const handleModelChartSeeErrorResultsClick = () => {
    const queryParams = new URLSearchParams();
    queryParams.set(
      MODEL_RUNS_TABLE_FILTERS,
      JSON.stringify([
        {
          property: 'status',
          operator: 'equals',
          value: ['error'],
        },
      ]),
    );
    navigateWithFilters('/report/model-runs', queryParams);
  };

  const handleModelPieChartClick = (status?: string) => {
    if (!status) {
      navigateWithFilters('/report/model-runs');
      return;
    }

    const queryParams = new URLSearchParams();
    queryParams.set(
      MODEL_RUNS_TABLE_FILTERS,
      JSON.stringify([
        {
          property: 'status',
          operator: 'equals',
          value: modelStatusFilterMap[status.toLowerCase()],
        },
      ]),
    );
    navigateWithFilters('/report/model-runs', queryParams);
  };

  const handleSummaryBoxClick = (key: string, statuses?: string[]) => {
    const queryParams = new URLSearchParams();

    const testTypeData = testTypes.find((type) => type.key === key);

    if (!testTypeData) return;

    const tableFilters: UrlFilters = buildTestTypeFilters(testTypeData);

    if (statuses && statuses.length > 0) {
      tableFilters.push({
        property: 'latestRunStatus',
        operator: 'equals',
        value: statuses.flatMap((status) => tableStatusFilterMap[status]),
      });
    }

    queryParams.set(TEST_RESULTS_TABLE_FILTERS, JSON.stringify(tableFilters));
    navigateWithFilters('/report/test-results', queryParams);
  };

  const invocation = useMemo(() => {
    if (!invocationData || !invocationData?.invocation_id) {
      return null;
    }

    const formattedTimestamp = localizeDate(invocationData.detected_at).format(
      'L LTS',
    );

    if (
      !(
        invocationData.command ||
        invocationData.selected ||
        invocationData.full_refresh
      )
    )
      return formattedTimestamp;

    return `${formattedTimestamp} | ${
      invocationData.command ? `dbt ${invocationData.command}` : ''
    } ${invocationData.selected ? `-s ${invocationData.selected}` : ''} ${
      invocationData.full_refresh ? '--full-refresh' : ''
    }`;
  }, [invocationData]);

  if (isEmpty) {
    return renderEmptyStatePage();
  }

  return (
    <DashboardContainer ref={containerRef}>
      <CenteringWrapper>
        <Header>
          <FlexSpace
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Breadcrumbs
              items={breadcrumbs || ['Dashboard']}
              showTreeButton={showToggleTreeViewButton}
            />
            {account?.ai_features_enabled && <GenerateAIButton />}
          </FlexSpace>
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Filters>
              <FilterPopover
                filterOptions={filterOptions}
                onSubmit={onFilterSubmit}
                multiselect
                sortOptions={false}
              />

              <FilterChipRow
                selectedFilters={urlFilters}
                filterOptions={filterOptions}
                clearFilter={clearFilter}
                onClearAll={clearAllFilters}
                onSubmit={onFilterSubmit}
              />
            </Filters>
            <InvocationWrapper>
              {invocation ? (
                <FlexCenter sx={{ gap: 1 }}>
                  <InvocationText>Invocation</InvocationText>
                  <Tooltip title={invocation}>
                    <Flex>
                      <InfoIcon />
                    </Flex>
                  </Tooltip>
                </FlexCenter>
              ) : (
                <InvocationText>Last {daysBack} Days</InvocationText>
              )}
            </InvocationWrapper>
          </Flex>
        </Header>

        <FlexColumn sx={{ gap: 1, width: '100%' }}>
          <StatusWrapper divider={<HorizontalDivider />}>
            <PieChartsWrapper divider={<PieSectionDivider />}>
              <>
                <FlexColumn sx={{ alignItems: 'center', gap: 1 }}>
                  <DashboardPieChart
                    value={sumArr(
                      resultsBreakdownData.map((item) => item.value),
                    ).toString()}
                    data={resultsBreakdownData}
                    label="Tests"
                    title="Test results breakdown"
                    tooltip={tooltips.resultsBreakdown}
                    onLabelClick={handleTestsPieLabelClick}
                    onValueClick={() =>
                      navigateWithFilters('/report/test-results')
                    }
                    loading={isDashboardStatsLoading}
                  />
                </FlexColumn>
                <FlexColumn sx={{ alignItems: 'center', gap: 1 }}>
                  <DashboardPieChart
                    value={sumArr(
                      tablesHealthData.map((item) => item.value),
                    ).toString()}
                    data={tablesHealthData}
                    label="Tables"
                    title="Tables health"
                    tooltip={tooltips.tablesHealth}
                    onLabelClick={handleTablesPieLabelClick}
                    onValueClick={() =>
                      navigateWithFilters('/report/test-results')
                    }
                    loading={isDashboardStatsLoading}
                  />
                </FlexColumn>
                <Legend categories={statusLegendCategories} />
              </>
              <Flex sx={{ alignItems: 'center', gap: 1 }}>
                <FlexColumn sx={{ alignItems: 'center', gap: 1 }}>
                  <DashboardPieChart
                    value={sumArr(
                      monitoredTablesData.map((item) => item.value),
                    ).toString()}
                    data={monitoredTablesData}
                    label="Tables"
                    title="Monitored tables"
                    tooltip={tooltips.monitoredTablesPie}
                    onValueClick={() =>
                      navigateWithFilters('/report/test-results')
                    }
                    loading={isDashboardStatsLoading}
                  />
                </FlexColumn>
                <Legend categories={monitoredLegendCategories} />
              </Flex>
            </PieChartsWrapper>
            <SummaryWrapper divider={<StatusDivider height={112} />}>
              {orderedSummaryKeys.map((key) => (
                <SummaryBox
                  key={key}
                  title={summaryData[key]?.title}
                  data={summaryData[key]}
                  tooltip={(tooltips as any)[key]}
                  onValueClick={() => handleSummaryBoxClick(key)}
                  onLabelClick={(statuses) =>
                    handleSummaryBoxClick(key, statuses)
                  }
                  loading={isDashboardStatsLoading}
                />
              ))}
            </SummaryWrapper>
          </StatusWrapper>
        </FlexColumn>

        {!invocation && (
          <ModelRunChartsWrapper>
            <ModelPieChartWrapper>
              <DashboardPieChart
                value={sumArr(
                  modelsHealthData.map((item) => item.value),
                ).toString()}
                data={modelsHealthData}
                label={runsCopy.runsPieLabel}
                title={runsCopy.runsTitle}
                tooltip={runsCopy.runsTooltip}
                onLabelClick={(status) => handleModelPieChartClick(status)}
                onValueClick={() => handleModelPieChartClick()}
                loading={isLoading}
              />
              <Legend categories={modelStatusLegendCategories} />
            </ModelPieChartWrapper>

            <ResponsiveChartContainer $width={807} $narrowWidth={390}>
              <DashboardChart
                data={modelFailuresChartData}
                xAxisDataKey="date"
                areaDataKey="value"
                width={807}
                height={260}
                title={runsCopy.errorsTitle}
                tooltip={runsCopy.errorsTooltip}
                color="fail"
                unit="Number"
                onSeeResultsClick={() => handleModelChartSeeErrorResultsClick()}
                loading={isLoading}
              />
            </ResponsiveChartContainer>
          </ModelRunChartsWrapper>
        )}
        {!invocation && (
          <ChartsWrapper>
            <ChartRow>
              <DashboardChart
                data={uniqueTestExecData}
                xAxisDataKey="date"
                areaDataKey="value"
                width={390}
                title="Unique tests executions"
                tooltip={tooltips.uniqueTestExecutions}
                color="success"
                unit="Number"
                onSeeResultsClick={() =>
                  navigateWithFilters('/report/test-runs')
                }
                loading={isLoading}
              />
              <DashboardChart
                data={failedTestsChartData}
                xAxisDataKey="date"
                areaDataKey="value"
                width={390}
                title="Test failures"
                tooltip={tooltips.testFailures}
                color="fail"
                unit="Number"
                yAxisDataMaxOverride={statusChartsDomainYDataMax}
                onSeeResultsClick={() => handleChartSeeResultsClick('failures')}
                loading={isLoading}
              />
              <DashboardChart
                data={warningTestsChartData}
                xAxisDataKey="date"
                areaDataKey="value"
                width={390}
                title="Test warnings"
                tooltip={tooltips.testWarnings}
                color="warning"
                unit="Number"
                yAxisDataMaxOverride={statusChartsDomainYDataMax}
                onSeeResultsClick={() => handleChartSeeResultsClick('warnings')}
                loading={isLoading}
              />
              <ResponsiveChartContainer $width={600} $narrowWidth={390}>
                <DashboardChart
                  data={monitoredTablesChartData}
                  xAxisDataKey="date"
                  areaDataKey="value"
                  width={600}
                  title="Monitored tables"
                  tooltip={tooltips.monitoredTables}
                  color="success"
                  unit="%"
                  domainY={[0, 100]}
                  tooltipFormatter={(val) => `${Math.round(Number(val))}%`}
                  loading={isLoading}
                />
              </ResponsiveChartContainer>
              <ResponsiveChartContainer $width={600} $narrowWidth={390}>
                <DashboardChart
                  data={tablesWithTestFailuresChartData}
                  xAxisDataKey="date"
                  areaDataKey="value"
                  width={600}
                  title="Tables with failed tests"
                  tooltip={tooltips.tablesWithFailedTests}
                  color="fail"
                  unit="Number"
                  loading={isLoading}
                />
              </ResponsiveChartContainer>
            </ChartRow>
          </ChartsWrapper>
        )}
      </CenteringWrapper>
    </DashboardContainer>
  );
};

export default Dashboard;
